"use client";

import Image from "next/image";
import { useTranslations } from "next-intl";

const FEATURE_KEYS = [
	{ titleKey: "feature1Title", descKey: "feature1Desc" },
	{ titleKey: "feature2Title", descKey: "feature2Desc" },
	{ titleKey: "feature3Title", descKey: "feature3Desc" },
	{ titleKey: "feature4Title", descKey: "feature4Desc" },
	{ titleKey: "feature5Title", descKey: "feature5Desc" },
] as const;

function CheckBullet() {
	return (
		<div
			className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-themePrimary400"
			aria-hidden
		>
			<svg className="h-3.5 w-3.5 text-white" viewBox="0 0 12 10" fill="none" aria-hidden>
				<path
					d="M1 5l3.5 3.5L11 1"
					stroke="currentColor"
					strokeWidth="2"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
			</svg>
		</div>
	);
}

export interface PrototypeToolingAdvantagesProps {
	translationNamespace: string;
	imageSrc?: string;
}

export default function PrototypeToolingAdvantages({
	translationNamespace,
	imageSrc = "/image/new-version/molding/injection-mold-tooling/advantages-of-prototype-tooling​.jpg",
}: PrototypeToolingAdvantagesProps) {
	const t = useTranslations(translationNamespace);

	return (
		<section className="w-full bg-white py-8 sm:py-10 md:py-12 lg:py-16">
			<div className="container">
				<div className="grid grid-cols-1 items-start gap-10 lg:grid-cols-2 lg:gap-12 xl:gap-16">
					<div className="relative mx-auto aspect-square w-full max-w-[520px] overflow-hidden bg-[#f5f5f5] lg:mx-0 lg:max-w-none">
						<Image
							src={imageSrc}
							alt={t("imageAlt")}
							fill
							className="object-cover"
							sizes="(max-width: 1024px) 100vw, 50vw"
						/>
					</div>

					<div className="min-w-0">
						<h2 className="mb-3 text-2xl font-bold leading-tight text-black sm:mb-4 sm:text-[30px] md:text-4xl">
							{t("title")}
						</h2>
						<p className="mb-8 text-sm leading-relaxed text-black sm:mb-10 md:text-base">{t("intro")}</p>
						<ul className="flex flex-col gap-6 sm:gap-7">
							{FEATURE_KEYS.map((key) => (
								<li key={key.titleKey} className="flex gap-3 sm:gap-4">
									<CheckBullet />
									<div className="min-w-0">
										<h3 className="mb-1.5 text-base font-semibold text-black sm:text-lg">
											{t(key.titleKey)}
										</h3>
										<p className="text-sm leading-relaxed text-black md:text-base">{t(key.descKey)}</p>
									</div>
								</li>
							))}
						</ul>
					</div>
				</div>
			</div>
		</section>
	);
}
